import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import './AthletePicker.css';

/**
 * Searchable dropdown to link a standout / spotlight to a registered athlete.
 *
 * Props:
 *  - value:        the currently linked athlete_user_id (string | null)
 *  - linkedName:   display name of the currently linked athlete (string | null)
 *  - onChange:     (userId|null, athlete|null) => void
 */
const AthletePicker = ({ value, linkedName, onChange }) => {
  const { searchAthletes, getAthletesByIds } = useAuth();
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(
    value ? { user_id: value, full_name: linkedName || 'Linked athlete' } : null
  );
  const wrapRef = useRef(null);
  const debounceRef = useRef(null);

  // Keep local selection in sync if the parent changes the value externally.
  useEffect(() => {
    if (!value) setSelected(null);
    else setSelected((prev) => (prev?.user_id === value ? prev : { user_id: value, full_name: linkedName || 'Linked athlete' }));
  }, [value, linkedName]);

  // Hydrate the chip with the linked athlete's real name / email / photo.
  useEffect(() => {
    let active = true;
    if (value && (!selected || selected.user_id !== value || (!selected.email && !selected.profile_image))) {
      getAthletesByIds([value]).then((res) => {
        if (active && res.success && res.data[0]) setSelected(res.data[0]);
      });
    }
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const runSearch = useCallback(async (q) => {
    setLoading(true);
    const res = await searchAthletes(q);
    setResults(res.success ? res.data : []);
    setLoading(false);
  }, [searchAthletes]);

  // Debounced search whenever the dropdown is open and the term changes.
  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(term.trim()), 250);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [term, open, runSearch]);

  // Close on outside click.
  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const handleSelect = (athlete) => {
    setSelected(athlete);
    onChange(athlete.user_id, athlete);
    setOpen(false);
    setTerm('');
  };

  const handleClear = (e) => {
    e.stopPropagation();
    setSelected(null);
    onChange(null, null);
  };

  return (
    <div className="athlete-picker" ref={wrapRef}>
      {selected ? (
        <div className="athlete-picker-selected" onClick={() => setOpen(true)}>
          {selected.profile_image ? (
            <img className="athlete-picker-avatar" src={selected.profile_image} alt="" />
          ) : (
            <span className="athlete-picker-avatar athlete-picker-avatar--initial">
              {(selected.full_name || '?').charAt(0).toUpperCase()}
            </span>
          )}
          <div className="athlete-picker-selected-info">
            <span className="athlete-picker-selected-name">{selected.full_name || 'Linked athlete'}</span>
            {selected.email && <span className="athlete-picker-selected-email">{selected.email}</span>}
          </div>
          <span className="athlete-picker-linked-badge">Linked</span>
          <button type="button" className="athlete-picker-clear" onClick={handleClear} aria-label="Unlink athlete">✕</button>
        </div>
      ) : (
        <input
          type="text"
          className="athlete-picker-input"
          placeholder="Search a registered athlete by name or email…"
          value={term}
          onChange={(e) => { setTerm(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
        />
      )}

      {open && !selected && (
        <div className="athlete-picker-dropdown">
          {loading ? (
            <div className="athlete-picker-state">Searching…</div>
          ) : results.length === 0 ? (
            <div className="athlete-picker-state">
              {term.trim() ? 'No matching athletes.' : 'Type to search registered athletes.'}
            </div>
          ) : (
            results.map((a) => (
              <button
                type="button"
                key={a.user_id}
                className="athlete-picker-option"
                onClick={() => handleSelect(a)}
              >
                {a.profile_image ? (
                  <img className="athlete-picker-avatar" src={a.profile_image} alt="" />
                ) : (
                  <span className="athlete-picker-avatar athlete-picker-avatar--initial">
                    {(a.full_name || '?').charAt(0).toUpperCase()}
                  </span>
                )}
                <div className="athlete-picker-option-info">
                  <span className="athlete-picker-option-name">{a.full_name || 'Unnamed athlete'}</span>
                  <span className="athlete-picker-option-meta">
                    {[a.sport, a.email].filter(Boolean).join('  •  ')}
                  </span>
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default AthletePicker;
